import React, { useEffect, useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ModeIcon from '@mui/icons-material/Mode';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import InclusionAssetEditDialog, { type InclusionAssetEditValue } from './InclusionAssetEditDialog';
import {
  type MasterInclusion,
  type MasterInclusionEdit,
  type PackageOption,
  TABLE_GRID_COLUMNS,
  filterPackageOptions,
} from './utils';

interface Props {
  inclusion: MasterInclusion;
  packageOptions: Array<PackageOption>;
  onChange: (id: string, nextValues: Partial<MasterInclusion>) => void;
  onSaveEdit: (inclusion: MasterInclusion, nextValues: MasterInclusionEdit) => Promise<void>;
}

function getAssignedPackagesLabel(selectedPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  const packageOptionIds = new Set(packageOptions.map((option) => option.id));
  const validPackageIds = selectedPackageIds.filter((packageId) => packageOptionIds.has(packageId));

  if (packageOptions.length === 0) {
    return 'No packages available';
  }

  if (validPackageIds.length === 0) {
    return 'No packages';
  }

  if (validPackageIds.length === packageOptions.length) {
    return 'All packages';
  }

  if (validPackageIds.length === 1) {
    return packageOptions.find((option) => option.id === validPackageIds[0])?.name ?? '1 package';
  }

  return `${validPackageIds.length} of ${packageOptions.length} packages`;
}

function getDraftInclusion(inclusion: MasterInclusion): InclusionAssetEditValue {
  return {
    category: inclusion.category,
    categoryId: inclusion.categoryId ?? null,
    currency: inclusion.currency ?? '',
    isHidden: !!inclusion.isHidden,
    name: inclusion.inclusion,
    rrp: inclusion.rrp ?? 0,
    type: inclusion.type ?? 'Flash',
    unit: inclusion.unit ?? '',
  };
}

function buildMasterInclusionEdit(value: InclusionAssetEditValue): MasterInclusionEdit {
  return {
    category: value.category,
    categoryId: value.categoryId,
    currency: value.currency,
    inclusion: value.name,
    isHidden: value.isHidden,
    rrp: value.rrp,
    type: value.type,
    unit: value.unit,
  };
}

export default function InclusionRow({ inclusion, packageOptions, onChange, onSaveEdit }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: inclusion.id });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [packageSearch, setPackageSearch] = useState('');
  const [draftInclusion, setDraftInclusion] = useState<InclusionAssetEditValue>(() => getDraftInclusion(inclusion));
  const visiblePackageOptions = filterPackageOptions(packageOptions, packageSearch);
  const assignedPackagesLabel = getAssignedPackagesLabel(inclusion.assignedPackageIds, packageOptions);
  useEffect(() => {
    if (!isEditDialogOpen) {
      setDraftInclusion(getDraftInclusion(inclusion));
    }
  }, [inclusion, isEditDialogOpen]);

  const closePackageDialog = () => {
    setIsPackageDialogOpen(false);
    setPackageSearch('');
  };

  const togglePackageAssignment = (packageId: string) => {
    const assignedPackageIds = inclusion.assignedPackageIds.includes(packageId)
      ? inclusion.assignedPackageIds.filter((assignedPackageId) => assignedPackageId !== packageId)
      : inclusion.assignedPackageIds.concat(packageId);

    onChange(inclusion.id, { assignedPackageIds });
  };

  const selectAllPackages = () => {
    onChange(inclusion.id, { assignedPackageIds: packageOptions.map((option) => option.id) });
  };

  const clearPackages = () => {
    onChange(inclusion.id, { assignedPackageIds: [] });
  };

  const handleCloseEditDialog = () => {
    setDraftInclusion(getDraftInclusion(inclusion));
    setIsEditDialogOpen(false);
  };

  const handleSaveEditDialog = async () => {
    setIsSavingEdit(true);

    try {
      await onSaveEdit(inclusion, buildMasterInclusionEdit(draftInclusion));
      setIsEditDialogOpen(false);
    } catch {
      return;
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <>
      <Box
        ref={setNodeRef}
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: TABLE_GRID_COLUMNS },
          opacity: isDragging ? 0.7 : 1,
          px: 2,
          py: 2,
          position: 'relative',
          transform: CSS.Transform.toString(transform),
          transition,
          zIndex: isDragging ? 1 : 'auto',
          '& .MuiInputBase-root': {
            bgcolor: 'background.paper',
          },
        }}
      >
        <IconButton
          size="small"
          aria-label={`Reorder ${inclusion.inclusion || 'inclusion'}`}
          sx={{ color: 'text.disabled', cursor: 'grab' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{inclusion.category}</Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{inclusion.inclusion}</Typography>
        </Box>

        <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
          <Switch
            size="small"
            checked={inclusion.highlighted}
            onChange={(event) =>
              onChange(inclusion.id, {
                highlighted: event.target.checked,
                luxPlusOnly: event.target.checked ? false : inclusion.luxPlusOnly,
              })
            }
            disabled={inclusion.luxPlusOnly}
          />
        </Stack>

        <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
          <Switch
            size="small"
            checked={inclusion.luxPlusOnly}
            onChange={(event) =>
              onChange(inclusion.id, {
                highlighted: event.target.checked ? false : inclusion.highlighted,
                luxPlusOnly: event.target.checked,
              })
            }
            disabled={inclusion.highlighted}
          />
        </Stack>

        <Box>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            disabled={packageOptions.length === 0}
            onClick={() => setIsPackageDialogOpen(true)}
            sx={{ justifyContent: 'flex-start', minHeight: 40, textAlign: 'left', textTransform: 'none' }}
          >
            {assignedPackagesLabel}
          </Button>
          <Dialog open={isPackageDialogOpen} onClose={closePackageDialog} fullWidth maxWidth="sm">
            <DialogTitle>Assigned packages</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <Stack
                spacing={0.5}
                sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', px: 2, pt: 2, pb: 0.5 }}
              >
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="Search packages"
                  value={packageSearch}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  onChange={(event) => setPackageSearch(event.target.value)}
                />
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button variant="text" size="small" onClick={selectAllPackages} sx={{ minHeight: 28 }}>
                    Select all
                  </Button>
                  <Button variant="text" size="small" onClick={clearPackages} sx={{ minHeight: 28 }}>
                    Clear
                  </Button>
                </Stack>
              </Stack>
              <Box sx={{ maxHeight: 420, overflowY: 'auto', py: 0.5 }}>
                {visiblePackageOptions.map((option) => (
                  <MenuItem key={option.id} onClick={() => togglePackageAssignment(option.id)} sx={{ gap: 1, py: 1 }}>
                    <Checkbox
                      size="small"
                      checked={inclusion.assignedPackageIds.includes(option.id)}
                      sx={{ p: 0.25 }}
                    />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
                {visiblePackageOptions.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="No packages found" />
                  </MenuItem>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="text" color="primary" onClick={closePackageDialog}>
                Done
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        <IconButton color="primary" aria-label="Edit inclusion" onClick={() => setIsEditDialogOpen(true)}>
          <ModeIcon />
        </IconButton>
      </Box>

      <InclusionAssetEditDialog
        isCustom={inclusion.isCustom}
        isSaving={isSavingEdit}
        open={isEditDialogOpen}
        value={draftInclusion}
        onChange={setDraftInclusion}
        onClose={handleCloseEditDialog}
        onSave={() => void handleSaveEditDialog()}
      />
    </>
  );
}
